import React, { FC, useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, Grid } from 'transfix-ui/core'
import { IError } from 'types/graphqlTypes'
import CompanyInfoFormSection from './CompanyInfoFormSection'
import ContactInfoFormSection from './ContactInfoFormSection'
import { useCarrierApplicationForm } from './hooks/useCarrierApplicationForm'
import useCarrierApplicationMutations from './hooks/useCarrierApplicationMutations'
import { TCarrierApplicationSchema } from './schema'

export interface ICarrierApplicationFormProps {}

const CarrierApplicationForm: FC<ICarrierApplicationFormProps> = () => {
  const history = useHistory()
  const [formErrors, setFormErrors] = useState<IError[]>([])

  const { create, loading } = useCarrierApplicationMutations()

  const {
    clearErrors,
    control,
    errors,
    handleSubmit,
    setError,
    trigger,
  } = useCarrierApplicationForm({
    defaultValues: {
      name: '',
      dbaName: '',
      mcNumber: '',
      ein: '',
      entityType: null,
      dotNumber: '',
      isDisadvantagedBusinessEntity: false,
      headquarter: {
        address: '',
        countryCode: 'US',
        city: '',
        stateCode: '',
        zipcode: '',
        contactPhone: '',
        contactPhoneExt: '',
      },
      contactEmail: '',
      accountingEmail: '',
      secondaryAccountingEmail: '',
    },
  })
  const hasErrors = Object.keys(errors).length > 0

  const createCarrier = useCallback(
    (formValues: TCarrierApplicationSchema) => {
      create(formValues)
        .then(({ carrierApplicationId, errors }) => {
          if (carrierApplicationId) {
            history.push(carrierApplicationId)
          }
          if (errors) {
            setFormErrors(errors)
          }
        })
        .catch(() => {
          setFormErrors([
            { key: 'form-error', message: 'There was an error creating this carrier' },
          ])
        })
    },
    [create, history]
  )

  const handleCreate = useCallback(() => {
    setFormErrors([])
    handleSubmit(data => createCarrier(data))()
  }, [createCarrier, handleSubmit])

  const handleCancel = useCallback(() => {
    history.goBack()
  }, [history])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} data-test='form-errors'>
        {formErrors.map(({ key, message }) => (
          <InlineMessage
            key={key}
            message={message}
            dataTest={`form-error-${key}`}
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            fullWidth
          />
        ))}
      </Grid>
      <Grid item xs={12} lg={5}>
        <CompanyInfoFormSection
          control={control}
          errors={errors}
          trigger={trigger}
          setError={setError}
          clearErrors={clearErrors}
        />
      </Grid>
      <Grid item xs={12} lg={7}>
        <ContactInfoFormSection control={control} errors={errors} />
      </Grid>
      <Grid item xs={12}>
        <Box display='flex' justifyContent='flex-end'>
          <Box mr={2}>
            <Button color='secondary' onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
          <Box>
            <Button
              onClick={handleCreate}
              data-test='carrier-form-submit'
              disabled={hasErrors || loading}>
              Create
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default CarrierApplicationForm
